import { type JSX, useState } from 'react';
import { Link, useLocation } from 'react-router';

import {
  Bell,
  Building2,
  ChevronRight,
  Copy,
  FlaskConical,
  Images,
  Landmark,
  MessageSquare,
  Plug,
  ScrollText,
  Users,
} from 'lucide-react';
import { Collapsible } from 'radix-ui';

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  useSidebar,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar';

interface Props {
  projectBasePath: string;
  isFinancialEntity: boolean;
}

const MasterProjectNav = ({ projectBasePath, isFinancialEntity }: Props): JSX.Element => {
  const { pathname } = useLocation();
  const { state: sidebarState } = useSidebar();
  const isSidebarCollapsed = sidebarState === 'collapsed';
  const [isOpen, setIsOpen] = useState(true);
  const notificationsPath = `${projectBasePath}/notifications`;
  const notificationsActivityPath = `${notificationsPath}/activity`;
  const notificationsRulesPath = `${notificationsPath}/rules`;
  const notificationsSettingsPath = `${notificationsPath}/settings`;
  const notificationsWebhooksPath = `${notificationsPath}/webhooks`;
  const isNotificationsRoute = pathname === notificationsPath || pathname.startsWith(`${notificationsPath}/`);
  const testingPath = `${projectBasePath}/testing`;
  const testingCasesPath = `${testingPath}/casos`;
  const testingScenariosPath = `${testingPath}/escenarios`;
  const testingRunsPath = `${testingPath}/runs`;
  const isTestingRoute = pathname === testingPath || pathname.startsWith(`${testingPath}/`);
  const mortgagePath = `${projectBasePath}/mortgage`;
  const mortgageConfigPath = `${mortgagePath}/config`;
  const mortgageDocumentsPath = `${mortgagePath}/documents`;
  const isMortgageRoute = pathname === mortgagePath || pathname.startsWith(`${mortgagePath}/`);

  return (
    <Collapsible.Root asChild open={isSidebarCollapsed || isOpen} onOpenChange={setIsOpen} className="group/master">
      <SidebarGroup className="shrink-0">
        <Collapsible.Trigger asChild>
          <SidebarGroupLabel
            asChild
            className="group-data-[collapsible=icon]:hidden text-white/50 uppercase font-body text-[11px] font-medium tracking-wider cursor-pointer hover:text-white/80"
          >
            <button type="button">
              <span>Administrar inmobiliaria</span>
              <ChevronRight className="ml-auto shrink-0 transition-transform group-data-[state=open]/master:rotate-90" />
            </button>
          </SidebarGroupLabel>
        </Collapsible.Trigger>
        <Collapsible.Content>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={pathname === `${projectBasePath}/config` || pathname === projectBasePath}
                  tooltip="Configuración inmobiliaria"
                >
                  <Link to={`${projectBasePath}/config`}>
                    <Building2 />
                    <span>Configuración inmobiliaria</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={pathname === `${projectBasePath}/prompt`} tooltip="Prompt base">
                  <Link to={`${projectBasePath}/prompt`}>
                    <ScrollText />
                    <span>Prompt base</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={pathname === `${projectBasePath}/descripciones`}
                  tooltip="Descripciones de proyecto"
                >
                  <Link to={`${projectBasePath}/descripciones`}>
                    <ScrollText />
                    <span>Descripciones de proyectos</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={pathname === `${projectBasePath}/multimedia`}
                  tooltip="Multimedia general"
                >
                  <Link to={`${projectBasePath}/multimedia`}>
                    <Images />
                    <span>Multimedia general</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={pathname === `${projectBasePath}/integrations`}
                  tooltip="Integraciones"
                >
                  <Link to={`${projectBasePath}/integrations`}>
                    <Plug />
                    <span>Integraciones</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {isFinancialEntity ? (
                <Collapsible.Root asChild defaultOpen={isMortgageRoute} className="group/mortgage">
                  <SidebarMenuItem>
                    <Collapsible.Trigger asChild>
                      <SidebarMenuButton isActive={isMortgageRoute} tooltip="Hipotecario">
                        <Landmark />
                        <span className="min-w-0 flex-1 truncate">Hipotecario</span>
                        <ChevronRight className="ml-auto shrink-0 transition-transform group-data-[state=open]/mortgage:rotate-90" />
                      </SidebarMenuButton>
                    </Collapsible.Trigger>
                    <Collapsible.Content>
                      <SidebarMenuSub className="mx-2 px-2 py-1">
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton asChild isActive={pathname === mortgageConfigPath}>
                            <Link to={mortgageConfigPath}>
                              <span>Configuración</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton asChild isActive={pathname === mortgageDocumentsPath}>
                            <Link to={mortgageDocumentsPath}>
                              <span>Documentos</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      </SidebarMenuSub>
                    </Collapsible.Content>
                  </SidebarMenuItem>
                </Collapsible.Root>
              ) : (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={isMortgageRoute} tooltip="Hipotecario">
                    <Link to={mortgageConfigPath}>
                      <Landmark />
                      <span>Hipotecario</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={pathname === `${projectBasePath}/demo-chat`} tooltip="Demo Chat">
                  <Link to={`${projectBasePath}/demo-chat`}>
                    <MessageSquare />
                    <span>Demo Chat</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <Collapsible.Root asChild defaultOpen={isNotificationsRoute} className="group/notifications">
                <SidebarMenuItem>
                  <Collapsible.Trigger asChild>
                    <SidebarMenuButton isActive={isNotificationsRoute} tooltip="Centro de notificaciones">
                      <Bell />
                      <span className="min-w-0 flex-1 truncate">Centro de notificaciones</span>
                      <ChevronRight className="ml-auto shrink-0 transition-transform group-data-[state=open]/notifications:rotate-90" />
                    </SidebarMenuButton>
                  </Collapsible.Trigger>
                  <Collapsible.Content>
                    <SidebarMenuSub className="mx-2 px-2 py-1">
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton asChild isActive={pathname === notificationsActivityPath}>
                          <Link to={notificationsActivityPath}>
                            <span>Actividad</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton asChild isActive={pathname === notificationsRulesPath}>
                          <Link to={notificationsRulesPath}>
                            <span>Reglas de detección</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton asChild isActive={pathname === notificationsSettingsPath}>
                          <Link to={notificationsSettingsPath}>
                            <span>Configuración</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton asChild isActive={pathname === notificationsWebhooksPath}>
                          <Link to={notificationsWebhooksPath}>
                            <span>Webhooks</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </Collapsible.Content>
                </SidebarMenuItem>
              </Collapsible.Root>
              <Collapsible.Root asChild defaultOpen={isTestingRoute} className="group/testing">
                <SidebarMenuItem>
                  <Collapsible.Trigger asChild>
                    <SidebarMenuButton isActive={isTestingRoute} tooltip="Centro de Testing">
                      <FlaskConical />
                      <span className="min-w-0 flex-1 truncate">Centro de Testing</span>
                      <ChevronRight className="ml-auto shrink-0 transition-transform group-data-[state=open]/testing:rotate-90" />
                    </SidebarMenuButton>
                  </Collapsible.Trigger>
                  <Collapsible.Content>
                    <SidebarMenuSub className="mx-2 px-2 py-1">
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton asChild isActive={pathname === testingCasesPath}>
                          <Link to={testingCasesPath}>
                            <span>Evaluaciones</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton asChild isActive={pathname === testingScenariosPath}>
                          <Link to={testingScenariosPath}>
                            <span>Escenarios</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton asChild isActive={pathname === testingRunsPath}>
                          <Link to={testingRunsPath}>
                            <span>Runs</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </Collapsible.Content>
                </SidebarMenuItem>
              </Collapsible.Root>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={pathname === `${projectBasePath}/users`} tooltip="Usuarios">
                  <Link to={`${projectBasePath}/users`}>
                    <Users />
                    <span>Usuarios</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={pathname === `${projectBasePath}/clone`} tooltip="Clonar">
                  <Link to={`${projectBasePath}/clone`}>
                    <Copy />
                    <span>Clonar</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </Collapsible.Content>
      </SidebarGroup>
    </Collapsible.Root>
  );
};

export default MasterProjectNav;
